import React, { useState } from 'react';
import { CompetitorData } from '../../types/dataTypes';

interface AdMetricsChartProps {
  platform: string;
  data: CompetitorData;
}

const AdMetricsChart: React.FC<AdMetricsChartProps> = ({ platform, data }) => {
  const [metric, setMetric] = useState('impressions');
  
  const metrics = [
    { id: 'impressions', label: 'Impressions' },
    { id: 'clicks', label: 'Clicks' },
    { id: 'ctr', label: 'CTR (%)' },
    { id: 'cpc', label: 'Avg. CPC' },
    { id: 'conversions', label: 'Conversions' }
  ];

  const getPlatformData = () => {
    switch(platform) {
      case 'facebook':
        return data.adMetrics.facebook;
      case 'linkedin':
        return data.adMetrics.linkedin;
      case 'google':
        return data.adMetrics.google;
      default:
        return data.adMetrics.facebook;
    }
  };

  const platformData = getPlatformData();
  const competitors = ['You', ...data.competitors.map(c => c.name)];
  const maxValue = Math.max(...competitors.map(comp => 
    comp === 'You' ? platformData[metric].you : platformData[metric].competitors[competitors.indexOf(comp) - 1]
  ));

  const formatValue = (value: number) => {
    if (metric === 'ctr') return `${value.toFixed(2)}%`;
    if (metric === 'cpc') return `$${value.toFixed(2)}`;
    return value >= 1000 ? `${(value / 1000).toFixed(1)}k` : value;
  };

  const getColor = (index: number) => {
    const colors = ['#3B82F6', '#F59E0B', '#10B981', '#8B5CF6', '#EC4899'];
    return index === 0 ? colors[0] : colors[(index % (colors.length - 1)) + 1];
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white">
          {platform.charAt(0).toUpperCase() + platform.slice(1)} Ad Performance
        </h3>
        <div className="flex space-x-1">
          {metrics.map(m => (
            <button
              key={m.id}
              onClick={() => setMetric(m.id)}
              className={`px-2 py-1 text-xs font-medium rounded-md transition-colors duration-200 ${
                metric === m.id
                  ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-100'
                  : 'text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700'
              }`}
            >
              {m.label}
            </button>
          ))}
        </div>
      </div>
      
      <div className="h-64">
        <div className="flex items-end h-full space-x-4 sm:space-x-8 px-4">
          {competitors.map((competitor, index) => {
            const value = competitor === 'You' 
              ? platformData[metric].you 
              : platformData[metric].competitors[index - 1];
            
            const heightPercent = (value / maxValue) * 85; // Max 85% to leave room for labels
            
            return (
              <div key={index} className="flex flex-col items-center flex-1">
                <div 
                  className="w-full relative group"
                  style={{ height: `${heightPercent}%` }}
                >
                  <div 
                    className={`absolute inset-x-2 sm:inset-x-4 bottom-0 rounded-t-md transition-all duration-300 group-hover:opacity-80 ${
                      competitor === 'You' ? 'bg-blue-500' : 'bg-gray-400 dark:bg-gray-500'
                    }`}
                    style={{ height: '100%' }}
                  ></div>
                  
                  {/* Tooltip */}
                  <div className="opacity-0 group-hover:opacity-100 absolute -top-10 left-1/2 transform -translate-x-1/2 bg-gray-800 text-white text-xs rounded py-1 px-2 whitespace-nowrap transition-opacity duration-200">
                    {formatValue(value)}
                  </div>
                </div>
                
                <div className="mt-2 text-xs text-center truncate w-full text-gray-700 dark:text-gray-300">
                  {competitor}
                </div>
              </div>
            );
          })}
        </div>
      </div>
      
      <div className="mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
        <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Key Insights:</h4>
        <ul className="space-y-1 text-sm text-gray-600 dark:text-gray-400">
          <li className="flex items-start">
            <span className="inline-block h-1.5 w-1.5 rounded-full bg-blue-500 mt-1.5 mr-2"></span>
            {platformData.insights[0]}
          </li>
          <li className="flex items-start">
            <span className="inline-block h-1.5 w-1.5 rounded-full bg-blue-500 mt-1.5 mr-2"></span>
            {platformData.insights[1]}
          </li>
          <li className="flex items-start">
            <span className="inline-block h-1.5 w-1.5 rounded-full bg-blue-500 mt-1.5 mr-2"></span>
            {platformData.insights[2]}
          </li>
        </ul>
      </div>
    </div>
  );
};

export default AdMetricsChart;